import { SelectionManager } from '@react-stately/selection';
import { Selection } from '@react-types/shared/src/selection';

export type WithResolveSelectAllToKeysKeys<
  T extends {
    onSelectionChange?: (selection: Selection) => void;
  }
> = Omit<T, 'onSelectionChange'> &
  (
    | {
        resolveSelectAllToKeys: true;
        onSelectionChange?: (keys: Exclude<Selection, 'all'>) => void;
      }
    | {
        resolveSelectAllToKeys?: false;
        onSelectionChange?: (keys: Selection) => void;
      }
  );

export interface SapphireSelectionManagerOptions {
  /**
   * By default, the behaviour of the selectAll method is to return `'all'` when the action is
   * performed. In this case, you can treat the all selection in any way you wish, but remember
   * that it refers to "all" items being selected, including those not yet loaded.
   *
   * This option allows you to change the behaviour of the selectAll method to return a detailed
   * selection array of keys when the action is performed.
   *
   * @default false
   */
  resolveSelectAllToKeys?: boolean;

  /**
   * Whether the entire collection component is disabled. It's something that's not currently
   * supported in react-aria, and we need to patch certain methods on selection manager to
   * make sure the interactions that invoke those methods are disabled.
   */
  isDisabled?: boolean;
}

const ORIGINAL_SELECT_ALL = Symbol('ORIGINAL_SELECT_ALL');

/**
 * This wrapper allows you to change the behaviour of the certain methods in a SelectionManager.
 */
const patchSelectionManager = (
  selectionManager: SelectionManager & { [ORIGINAL_SELECT_ALL]?: () => void },
  {
    resolveSelectAllToKeys = false,
    isDisabled,
  }: SapphireSelectionManagerOptions
): void => {
  selectionManager[ORIGINAL_SELECT_ALL] =
    selectionManager[ORIGINAL_SELECT_ALL] ?? selectionManager.selectAll;
  selectionManager.selectAll = function patchedSelectAll() {
    if (isDisabled) {
      return;
    }
    if (
      resolveSelectAllToKeys &&
      !this.isSelectAll &&
      this.selectionMode === 'multiple'
    ) {
      /**
       * The `getSelectAllKeys` in the original selection manager returns the exact data we need in
       * this case, when `resolveSelectAllToKeys` is set to true. Unfortunately, the function
       * is private in the selection manager, so we need to use this workaround to access it.
       * There are tests that verify this functionality, so, even though this is a bit of a hack,
       * we can be sure that if the function changes in the future, we will know.
       */
      // @ts-expect-error -- explaination above
      return this.setSelectedKeys(selectionManager.getSelectAllKeys());
    }

    return selectionManager[ORIGINAL_SELECT_ALL]?.call(this);
  };
};

export default patchSelectionManager;
